import React, { useState, useEffect, useCallback, useMemo } from "react";

import api from "./api";

function formatChf(n) {
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} CHF`;
}

function contractLabel(base) {
  if (base === "HOURLY") return "À l'heure";
  return "Hebdomadaire";
}

function Payroll({ user, showToast }) {
  const isManager = user.role === "manager";
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthIso, setMonthIso] = useState(() => api.currentMonthISO());
  const [loading, setLoading] = useState(true);
  const [payroll, setPayroll] = useState(null);
  const [worked, setWorked] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const weekIso = useMemo(
    () => api.addDays(api.currentMondayISO(), weekOffset * 7),
    [weekOffset]
  );

  const loadManager = useCallback(async () => {
    setLoading(true);
    try {
      const [p, w] = await Promise.all([
        api.getPayroll(weekIso),
        api.getWorkedKpi("week", null, weekIso),
      ]);
      setPayroll(p);
      setWorked(w);
    } catch (err) {
      showToast?.(String(err?.data?.error || err?.message || "Erreur chargement paie"));
      setPayroll(null);
      setWorked(null);
    } finally {
      setLoading(false);
    }
  }, [weekIso, showToast]);

  const loadEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([api.getPayroll(), api.getMonthlyHours(monthIso)]);
      setPayroll(p);
      setMonthly(m);
      setWorked(null);
    } catch (err) {
      showToast?.(String(err?.data?.error || err?.message || "Erreur chargement paie"));
      setPayroll(null);
      setMonthly(null);
    } finally {
      setLoading(false);
    }
  }, [monthIso, showToast]);

  useEffect(() => {
    if (isManager) loadManager();
    else loadEmployee();
  }, [isManager, loadManager, loadEmployee]);

  const items = payroll?.items ?? [];
  const totals = payroll?.totals ?? { hours: 0, gross: 0 };
  const workedItems = worked?.items ?? [];

  return (
    <div className="page-content">
      <h2 style={{ marginTop: 0, fontSize: "clamp(1.25rem, 4vw, 1.6rem)", color: "#0f172a" }}>
        Préparation de paie
      </h2>
      <p
        style={{
          marginBottom: "20px",
          color: "#555",
          fontSize: "clamp(0.85rem, 2vw, 1rem)",
        }}
      >
        {isManager
          ? "Données issues de l'API (contrats, taux, masse salariale indicative pour la semaine choisie)."
          : "Indicatif basé sur ton contrat et les heures enregistrées dans le planning pour le mois choisi."}
      </p>

      {isManager && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
            marginBottom: "16px",
            fontSize: "0.85rem",
          }}
        >
          <span style={{ fontWeight: 600 }}>Semaine (lundi) :</span>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o - 1)}
            style={{
              borderRadius: "999px",
              border: "1px solid #cbd5f5",
              background: "white",
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            ◀
          </button>
          <code style={{ fontSize: "0.85rem", background: "#f1f5f9", padding: "4px 8px", borderRadius: "8px" }}>
            {weekIso}
          </code>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o + 1)}
            style={{
              borderRadius: "999px",
              border: "1px solid #cbd5f5",
              background: "white",
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            ▶
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            style={{
              borderRadius: "999px",
              border: "1px solid #0f4dcc",
              background: "white",
              color: "#0f4dcc",
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            Semaine actuelle
          </button>
        </div>
      )}

      {!isManager && (
        <div style={{ marginBottom: "16px", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ fontWeight: 600 }}>
            Mois
            <input
              type="month"
              value={monthIso}
              onChange={(e) => setMonthIso(e.target.value)}
              style={{
                marginLeft: "8px",
                padding: "6px 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5f5",
              }}
            />
          </label>
        </div>
      )}

      {loading ? (
        <p style={{ color: "#64748b" }}>Chargement…</p>
      ) : (
        <>
          {isManager && (
            <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
              <div
                style={{
                  background: "white",
                  padding: "16px 20px",
                  borderRadius: "12px",
                  boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
                  minWidth: "200px",
                }}
              >
                <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "4px" }}>
                  Volume contractuel total (h / sem.)
                </div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{Number(totals.hours).toFixed(2)} h</div>
              </div>
              <div
                style={{
                  background: "white",
                  padding: "16px 20px",
                  borderRadius: "12px",
                  boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
                  minWidth: "200px",
                }}
              >
                <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "4px" }}>
                  Masse brute indicative (semaine)
                </div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{formatChf(totals.gross)}</div>
              </div>
            </div>
          )}

          <div
            style={{
              overflowX: "auto",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
              border: "1px solid #e2e8f0",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isManager ? 720 : 400 }}>
              <thead>
                <tr style={{ background: "#0f172a", color: "white", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px" }}>Employé</th>
                  <th style={{ padding: "10px 12px" }}>Base</th>
                  <th style={{ padding: "10px 12px" }}>h / sem.</th>
                  <th style={{ padding: "10px 12px" }}>Taux</th>
                  <th style={{ padding: "10px 12px" }}>Brut indicatif / sem.</th>
                  {isManager && <th style={{ padding: "10px 12px" }}>Heures réelles (semaine)</th>}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={isManager ? 6 : 5} style={{ padding: "16px", color: "#64748b" }}>
                      Aucun employé actif.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => {
                    const wh = workedItems.find((x) => x.id === row.id);
                    return (
                      <tr key={row.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>{row.name}</td>
                        <td style={{ padding: "10px 12px", fontSize: "0.88rem" }}>
                          {contractLabel(row.contractBase)}
                        </td>
                        <td style={{ padding: "10px 12px" }}>{Number(row.hoursPerWeek).toFixed(2)}</td>
                        <td style={{ padding: "10px 12px" }}>{Number(row.hourlyRate).toFixed(2)} CHF</td>
                        <td style={{ padding: "10px 12px" }}>{formatChf(row.grossWeekly)}</td>
                        {isManager && (
                          <td style={{ padding: "10px 12px" }}>
                            {wh ? `${Number(wh.workedHours).toFixed(2)} h` : "—"}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!isManager && monthly && (
            <div
              style={{
                marginTop: "20px",
                background: "white",
                padding: "16px 18px",
                borderRadius: "12px",
                boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ margin: "0 0 8px", fontSize: "1rem", color: "#0f172a" }}>
                Heures dans le mois ({monthly.month})
              </h3>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", marginBottom: "10px" }}>
                Période : {monthly.from} → {monthly.to}. Priorité aux heures réelles saisies, sinon le planning
                prévu.
              </p>
              {monthly.items?.length ? (
                <ul style={{ margin: 0, paddingLeft: "18px", color: "#0f172a" }}>
                  {monthly.items.map((it) => (
                    <li key={it.id} style={{ marginBottom: "4px" }}>
                      <strong>{it.name}</strong> : {Number(it.hoursInMonth).toFixed(2)} h
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, color: "#94a3b8" }}>Aucune donnée pour ce mois.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Payroll;
